<template>
  <div class="tracking-management">
    <router-view/>

    <layout-logged-frame :title="$t('tracking')">
      <template v-slot:header-actions>
        <Can action="create" subject="clicky-exports">
          <v-btn
            color="primary"
            :loading="isExporting"
            :disabled="isExporting"
            @click="exportClickyConfig"
          >
            <v-icon>cloud_upload</v-icon>
            {{ $t('export_clicky_config') }}
          </v-btn>
        </Can>

        <v-btn
          variant="outlined"
          color="primary"
          @click="openBulkUploadModal"
        >
          <v-icon>upload</v-icon>
          {{ $t('bulk_upload') }}
        </v-btn>

        <v-btn
          color="primary"
          @click="openNewAdvertiserModal"
        >
          <v-icon>add</v-icon>
          {{ $t('new_advertiser') }}
        </v-btn>
      </template>

      <template v-slot:content>
        <AdvertiserFilters @filter="onFilter" @clearFilters="onClearFilters" id="advertiser-filters"/>
        <table-base
          :columns="columns"
          :items="advertisers"
          :pages="totalPages"
          :pagination="pagination"
          :is-loading="isLoading"
          has-custom-items-template
          :on-select-item="selectAdvertiser"
          :on-update-pagination="onUpdatePagination"
          :on-change-page="onChangePage"
        >
          <template v-slot:item-cell="scope" >
            <template v-if="scope.cell.column.value === 'platforms'">
              <div class="d-flex flex-wrap gap-6">
                <PlatformPanel
                  v-for="platform in scope.cell.item.platforms"
                  :key="platform.code"
                  :country="platform"
                />
              </div>
            </template>

            <template v-else>
              {{ displayColumnValue(scope.cell.item, scope.cell.column.value) }}
            </template>
          </template>
        </table-base>
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
  import AdvertiserFilters from '../../components/forms/filters/TrackingAdvertisers';
  import { isStr, extractNestedProp, isObj } from '../../utils';
  import columns from '../../config/tracking/columnsAdvertisers';
  import PlatformPanel from '@/components/panels/Platform.vue';

  export default {
    components: {
      AdvertiserFilters,
      PlatformPanel,
    },
    data() {
      return {
        columns,
        pagination: {
          order: 'asc',
          page: 1,
          rowsPerPage: 15,
          sortBy: 'title',
          totalItems: 0,
        },
        totalPages: 0,
        query: {},
        isLoading: false,
        isExporting: false,
      };
    },
    computed: {
      advertisers() {
        return this.$store.getters['tracking/advertisers'];
      },
      totalCount() {
        return this.pagination.totalItems || 0;
      },
    },
    watch: {
      '$route' (to) {
        if (to.query?.status === 'success') {
          this.isLoading = true;
          this.getAdvertisers(this.query);
        }
      }
    },
    methods: {
      openBulkUploadModal() {
        this.$router.push({ name: 'tracking-management-bulk-upload' });
      },
      exportClickyConfig() {
        this.isExporting = true;

        return this.$store
          .dispatch('tracking/exportClickyConfig')
          .catch((err) => err)
          .finally(() => {
            this.isExporting = false;
          });
      },
      selectAdvertiser(advertiser) {
        this.$store.dispatch('tracking/setActiveAdvertiser', advertiser);
        this.$router.push({ name: 'tracking-management-advertiser', params: { id: advertiser.id } });
      },
      async getAdvertisers(query) {
        this.isLoading = true;
        query.per_page = this.pagination.rowsPerPage;

        return this.$store
          .dispatch('tracking/getAdvertisers', query)
          .then((res) => {
            const { advertisersMeta } = res;
            const { total, current_page, last_page } = advertisersMeta;

            this.pagination.page = current_page;
            this.pagination.totalItems = total;
            this.totalPages = last_page;

            return res;
          })
          .catch((err) => err)
          .finally(() => {
            this.isLoading = false;
          });
      },
      onUpdatePagination({ page, order, sortBy }) {
        const { query, pagination } = this;
        this.pagination.order = order;
        this.pagination.sortBy = isStr(sortBy) ? sortBy : pagination.sortBy;

        const nextQuery = {
          ...query,
          page,
          order,
          sort: isStr(sortBy) ? sortBy : pagination.sortBy,
        };

        this.query = nextQuery;
        this.getAdvertisers(nextQuery);
      },
      onChangePage(page) {
        const { query, pagination } = this;
        const { order, sortBy } = pagination;
        const nextQuery = {
          ...query,
          page,
          order,
          sort: sortBy,
        };

        this.query = nextQuery;
        this.getAdvertisers(nextQuery)
          .then(() => {
            // scroll to top
            const target = document.querySelector('#advertiser-filters');

            if (!isObj(target)) {
              return false;
            }

            target.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          })
      },
      onFilter(filters) {
        this.query = {
          ...this.query,
          page: 1,
          ...filters,
        };

        this.getAdvertisers(this.query);
      },
      onClearFilters() {
        const { sortBy } = this.pagination;
        this.query = { sort: sortBy };

        this.getAdvertisers(this.query);
      },
      openNewAdvertiserModal() {
        this.$router.push({ name: 'tracking-management-new-advertiser' });
      },
      openEditAdvertiserModal(id) {
        this.$router.push({ name: 'tracking-management-advertiser', params: { id } });
      },
      displayColumnValue(item, columnValue) {
        return extractNestedProp(item, columnValue);
      },
    },
    // todo: change this when merging with the vue 3 branch
    metaInfo() {
      return {
        title: this.$t('tracking_management'),
      };
    },
  };
</script>

<style lang="scss">
</style>
